import React, { useState } from "react";
import GratitudeTerminal from "../ui/GratitudeTerminal";
import { useJ2PuzzleState } from "../../../../hooks/useJ2PuzzleState";

const NEURAL_NODES = [
  { id: "S1",  x: 80,  y: 155, label: ["Stimulus", "Visuel"],    type: "input" },
  { id: "S2",  x: 80,  y: 285, label: ["Stimulus", "Auditif"],   type: "input" },
  { id: "P1",  x: 240, y: 95,  label: ["Cortex", "Préfrontal"],  type: "processing" },
  { id: "P2",  x: 240, y: 220, label: ["Amygdale"],              type: "processing" },
  { id: "P3",  x: 240, y: 345, label: ["Relai", "Thalamique"],   type: "processing" },
  { id: "M1",  x: 420, y: 125, label: ["Hippocampe"],            type: "memory" },
  { id: "M2",  x: 420, y: 270, label: ["Cortex", "Cingulaire"],  type: "memory" },
  { id: "E1",  x: 590, y: 150, label: ["Réponse", "Empathique"], type: "output" },
  { id: "E2",  x: 590, y: 290, label: ["Réponse", "Gratitude"],  type: "output" },
  { id: "MOD", x: 375, y: 45,  label: ["Modulateur", "Cognitif"],type: "modulator" },
];

const CORRECT_CONNECTIONS = [
  ["S1","P2"],["S1","P3"],
  ["S2","P2"],["S2","P1"],
  ["P1","M1"],["P1","M2"],
  ["P2","M1"],["P2","M2"],
  ["P3","MOD"],["MOD","E1"],
  ["M1","E1"],["M1","E2"],
  ["M2","E1"],["M2","E2"],
];

const NODE_COLORS = {
  input:      { fill:"#1e3a8a", stroke:"#60a5fa", glow:"rgba(96,165,250,.35)",  label:"Entrée" },
  processing: { fill:"#7c2d12", stroke:"#fb923c", glow:"rgba(251,146,60,.35)",  label:"Traitement" },
  memory:     { fill:"#4c1d95", stroke:"#c084fc", glow:"rgba(192,132,252,.35)", label:"Mémoire" },
  output:     { fill:"#064e3b", stroke:"#34d399", glow:"rgba(52,211,153,.35)",  label:"Sortie" },
  modulator:  { fill:"#831843", stroke:"#f472b6", glow:"rgba(244,114,182,.35)", label:"Modulateur" },
};

const CONNECTION_RULES = [
  { color:"#fb923c", title:"Amygdale (P2)",         text:"Impliquée dans le conditionnement émotionnel — les deux stimuli (visuel et auditif) y convergent, et elle alimente les deux zones de mémoire." },
  { color:"#fb923c", title:"Relai thalamique (P3)",  text:"Spécialisé dans le traitement visuel uniquement. Ce signal est ensuite modulé avant d'atteindre la réponse empathique — il ne va pas directement en mémoire." },
  { color:"#fb923c", title:"Cortex préfrontal (P1)", text:"Lié au langage et à la cognition. Activé par le stimulus auditif uniquement, il redistribue vers les deux zones de mémoire." },
  { color:"#c084fc", title:"Hippocampe (M1)",        text:"Zone centrale de la mémoire. Reçoit du cortex préfrontal et de l'amygdale, et contribue aux deux réponses émotionnelles." },
  { color:"#c084fc", title:"Cortex cingulaire (M2)", text:"Fonctionne en parallèle de l'hippocampe dans ce circuit — mêmes sources en entrée, mêmes sorties émotionnelles." },
  { color:"#f472b6", title:"Modulateur (MOD)",       text:"Reçoit exclusivement du relai thalamique. Il filtre ce signal visuel et le transmet uniquement vers la réponse empathique, pas vers la gratitude." },
];

function NeuralSVG({ connections, onConnectionChange, onSelectNode }) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [isGlitching, setIsGlitching] = useState(false);
  const [errorConn, setErrorConn] = useState(null);

  const getPos = (id) => { const n = NEURAL_NODES.find(n => n.id === id); return {x:n.x, y:n.y}; };

  const handleClick = (nodeId) => {
    if (!selectedNode) {
      setSelectedNode(nodeId);
      onSelectNode?.(nodeId);
      return;
    }
    if (selectedNode === nodeId) {
      setSelectedNode(null);
      onSelectNode?.(null);
      return;
    }
    const newConn = [selectedNode, nodeId].sort();
    const exists = connections.some(c => c[0]===newConn[0] && c[1]===newConn[1]);
    if (!exists) {
      const ok = CORRECT_CONNECTIONS.some(
        c => (c[0]===newConn[0]&&c[1]===newConn[1])||(c[0]===newConn[1]&&c[1]===newConn[0])
      );
      if (ok) {
        onConnectionChange([...connections, newConn]);
      } else {
        setErrorConn(newConn); setIsGlitching(true);
        setTimeout(() => { setErrorConn(null); setIsGlitching(false); onConnectionChange([]); }, 1400);
      }
    }
    setSelectedNode(null);
    onSelectNode?.(null);
  };

  const removeConn = (i) => onConnectionChange(connections.filter((_,idx)=>idx!==i));
  const connected = new Set(connections.flatMap(([a,b])=>[a,b]));

  return (
    <div className={isGlitching ? "animate-[glitch_0.3s_ease-in-out_4]" : ""} style={{position:"relative"}}>

      <svg viewBox="0 0 690 410" style={{width:"100%",height:"auto",display:"block"}}>
        <defs>
          <linearGradient id="nc-ok" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" stopOpacity=".85"/>
            <stop offset="100%" stopColor="#34d399" stopOpacity=".85"/>
          </linearGradient>
          <linearGradient id="nc-err" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity=".9"/>
            <stop offset="100%" stopColor="#f87171" stopOpacity=".9"/>
          </linearGradient>
          <pattern id="nc-bg" width="32" height="32" patternUnits="userSpaceOnUse">
            <circle cx="16" cy="16" r="1" fill="rgba(99,102,241,.07)"/>
          </pattern>
          <filter id="nc-glow">
            <feGaussianBlur stdDeviation="4" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        <rect width="690" height="410" fill="url(#nc-bg)"/>

        {/* Connexion erreur */}
        {errorConn && (()=>{
          const s=getPos(errorConn[0]),e=getPos(errorConn[1]);
          return <line x1={s.x} y1={s.y} x2={e.x} y2={e.y}
            stroke="url(#nc-err)" strokeWidth="5" strokeDasharray="10,5"
            filter="url(#nc-glow)" className="animate-pulse"/>;
        })()}

        {/* Connexions valides */}
        {connections.map((c,i)=>{
          const s=getPos(c[0]),e=getPos(c[1]);
          return (
            <g key={i} onClick={()=>removeConn(i)} style={{cursor:"pointer"}}>
              <line x1={s.x} y1={s.y} x2={e.x} y2={e.y}
                stroke="url(#nc-ok)" strokeWidth="3.5" opacity=".9" filter="url(#nc-glow)"/>
              <circle r="5" fill="#34d399" opacity=".85">
                <animateMotion dur="1.8s" repeatCount="indefinite"
                  path={`M ${s.x} ${s.y} L ${e.x} ${e.y}`}/>
              </circle>
            </g>
          );
        })}

        {/* Nœuds */}
        {NEURAL_NODES.map(node=>{
          const isSel = selectedNode===node.id;
          const isCon = connected.has(node.id);
          const col = NODE_COLORS[node.type];
          const R = 38;
          return (
            <g key={node.id} onClick={()=>handleClick(node.id)} style={{cursor:"pointer"}}>
              {isCon && (
                <circle cx={node.x} cy={node.y} r={R+14} fill={col.glow} opacity=".3">
                  <animate attributeName="r" values={`${R+10};${R+18};${R+10}`} dur="2.2s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values=".2;.4;.2" dur="2.2s" repeatCount="indefinite"/>
                </circle>
              )}
              {isSel && (
                <circle cx={node.x} cy={node.y} r={R+9}
                  fill="none" stroke={col.stroke} strokeWidth="2.5" strokeDasharray="7,4">
                  <animateTransform attributeName="transform" type="rotate"
                    from={`0 ${node.x} ${node.y}`} to={`360 ${node.x} ${node.y}`}
                    dur="1.8s" repeatCount="indefinite"/>
                </circle>
              )}
              <circle cx={node.x} cy={node.y} r={R}
                fill={isSel ? col.stroke : col.fill}
                stroke={col.stroke} strokeWidth="2.5" filter="url(#nc-glow)"
                className="hover:brightness-125 transition-all"/>
              {/* ID */}
              <text x={node.x} y={node.y-(node.label.length>1?11:4)}
                textAnchor="middle" fontSize="16" fontWeight="800" fill="white"
                style={{fontFamily:"ui-monospace,monospace",pointerEvents:"none"}}>
                {node.id}
              </text>
              {/* Labels lisibles */}
              {node.label.map((line,li)=>(
                <text key={li}
                  x={node.x} y={node.y+(node.label.length>1?5:4)+li*13}
                  textAnchor="middle" fontSize="10.5" fill="rgba(226,232,240,.9)"
                  style={{pointerEvents:"none"}}>
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>

      <style>{`
        @keyframes glitch {
          0%,100%{transform:translateX(0);filter:brightness(1)}
          15%{transform:translateX(-5px);filter:brightness(1.6) hue-rotate(180deg)}
          30%{transform:translateX(5px);filter:brightness(.6) hue-rotate(-180deg)}
          50%{transform:translateX(-3px);filter:brightness(1.4)}
          70%{transform:translateX(3px);filter:brightness(.8)}
        }
      `}</style>
    </div>
  );
}

export default function NeuralPuzzleContainer({ code, playerId, pseudo="Joueur 2", missionUnlock, missionResolve, players=[] }) {
  const [showRules, setShowRules] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const targetPseudo = players.find(p => p.pageVariant === 5)?.pseudo || null;
  const [puzzleState, setPuzzleState, loaded] = useJ2PuzzleState({
    code, playerId, puzzleType:"neural",
    initialState:{ connections:[], solved:false, password:"" },
  });

  if (!loaded) return (
    <div className="flex items-center justify-center h-40">
      <div className="text-slate-400 text-sm">Chargement de l'énigme...</div>
    </div>
  );

  const handleConnectionChange = (connections) => setPuzzleState(prev=>({...prev, connections}));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>

      {/* ── Barre de contrôles ── */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
        <div style={{fontSize:12,color:"rgba(148,163,184,.7)",fontFamily:"ui-monospace,monospace"}}>
          Connexions : <span style={{color:"#34d399",fontWeight:700}}>{puzzleState.connections.length}</span>/14
          <span style={{color:"rgba(100,116,139,.5)",margin:"0 6px"}}>·</span>
          <span style={{fontSize:11,color:"rgba(239,68,68,.6)"}}>
            Connexion invalide = réinitialisation du schéma
          </span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button
            onClick={()=>setShowRules(r=>!r)}
            style={{
              padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer",
              borderRadius:7, border:"1px solid",
              background: showRules ? "rgba(99,102,241,.25)" : "rgba(255,255,255,.05)",
              borderColor: showRules ? "rgba(99,102,241,.6)" : "rgba(255,255,255,.1)",
              color: showRules ? "rgba(165,180,252,.95)" : "rgba(148,163,184,.8)",
              fontFamily:"ui-monospace,monospace", letterSpacing:".05em",
              transition:"all .15s",
            }}
          >
            {showRules ? "▲ Masquer les règles" : "📋 Voir les règles"}
          </button>
          <button
            onClick={()=>handleConnectionChange([])}
            style={{padding:"5px 12px",fontSize:11,cursor:"pointer",borderRadius:7,
              border:"1px solid rgba(255,255,255,.08)",background:"rgba(255,255,255,.04)",
              color:"rgba(100,116,139,.7)",fontFamily:"ui-monospace,monospace",transition:"all .15s"}}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* ── Panneau règles (dépliable, au-dessus du circuit) ── */}
      {showRules && (
        <div style={{
          borderRadius:10, border:"1px solid rgba(99,102,241,.25)",
          background:"rgba(15,10,40,.95)", padding:"14px 16px",
          display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px 16px",
        }}>
          {CONNECTION_RULES.map((r,i)=>(
            <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
              <div style={{width:9,height:9,borderRadius:"50%",background:r.color,flexShrink:0,marginTop:3}}/>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"rgba(226,232,240,.9)",marginBottom:2}}>{r.title}</div>
                <div style={{fontSize:11,color:"rgba(148,163,184,.75)",lineHeight:1.5}}>{r.text}</div>
              </div>
            </div>
          ))}
          <div style={{gridColumn:"1/-1",marginTop:4,paddingTop:8,borderTop:"1px solid rgba(255,255,255,.06)",display:"flex",gap:16,flexWrap:"wrap"}}>
            {Object.entries(NODE_COLORS).map(([,c])=>(
              <div key={c.label} style={{display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:9,height:9,borderRadius:"50%",background:c.stroke}}/>
                <span style={{fontSize:10,color:"rgba(148,163,184,.65)"}}>{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Circuit full width ── */}
      <div style={{
        borderRadius:12, border:"1px solid rgba(255,255,255,.07)",
        background:"linear-gradient(135deg,#020510,#06102a 50%,#020510)",
        overflow:"hidden",
      }}>
        <NeuralSVG
          connections={puzzleState.connections}
          onConnectionChange={handleConnectionChange}
          onSelectNode={setSelectedNode}
        />
      </div>

      {/* Badge nœud sélectionné — sous le circuit, jamais caché */}
      <div style={{minHeight:28}}>
        {selectedNode && (
          <div style={{
            display:"inline-flex", alignItems:"center", gap:8,
            background:"rgba(251,191,36,.12)", border:"1px solid rgba(251,191,36,.4)",
            borderRadius:20, padding:"4px 16px", fontSize:11,
            color:"rgba(251,191,36,.95)", fontFamily:"ui-monospace,monospace",
          }}>
            <span style={{fontSize:14}}>◉</span>
            <span><strong>{selectedNode}</strong> sélectionné — cliquez un second nœud pour créer la connexion</span>
          </div>
        )}
      </div>

      {/* ── Terminal ── */}
      <GratitudeTerminal
        connections={puzzleState.connections}
        onSolved={(pwd)=>{
          setPuzzleState({connections:puzzleState.connections,solved:true,password:pwd});
          missionResolve?.({key:"s2.p2.neural.found"});
          missionUnlock?.({key:"s2.p2.password.found",text:`${pseudo} a trouvé le mot de passe NEURAL.`});
        }}
        password={puzzleState.password}
        solved={puzzleState.solved}
        targetPseudo={targetPseudo}
      />
    </div>
  );
}